import { HeaderCallToAction } from './hooks/useHeaderCallToAction/enum';
import type { PageViewed } from './telemetry';
import { Page } from './telemetry/page';

const External = {
  CONTACT: { path: 'https://hover.to/contact' },
  SUPPORT_EMAIL: { path: 'mailto:support@hover.to' },
  CAREERS: { path: '/jobs' },
  ESTIMATION_BANNER: { path: 'https://hover.to/estimate-and-close/' },
  ESTIMATION_CREATE: {
    path: '/ehi/#/estimator/questions/select_templates?jobId=:jobId',
  },
  ESTIMATION_DETAIL: {
    path: '/ehi/#/estimator/estimates/:estimateGroupId/details?jobId=:jobId',
  },
  ESTIMATION_LEARN_MORE: {
    path: 'https://help.hover.to/en/collections/3046380-transform',
  },
  HOMEOWNER_RENDERINGS: { path: '/homeowner-renderings' },
  PRICING: { path: '/pricing' },
  PRIVACY_POLICY: { path: 'https://hover.to/privacy' },
  PRODUCT: { path: '/product' },
  PROPERTY_DETAILS: { path: '/ui/#/property/:jobId' },
  RENDERINGS: { path: '/renderings' },
  RENDERINGS_NEW: { path: '/renderings/new' },
  RENDERINGS_LEARN_MORE_PRO: {
    path: '/design-images',
  },
  RENDERINGS_LEARN_MORE_HOMEOWNER: {
    path: '/homeowner-designs',
  },
  SOCIAL_FACEBOOK: { path: 'https://www.facebook.com/hover3d' },
  SOCIAL_TWITTER: { path: 'https://twitter.com/hover3d' },
  SOCIAL_YOUTUBE: { path: 'https://www.youtube.com/user/Hover3D' },
  START: { path: '/ui/#/properties' },
  MANAGE_TEAM: { path: '/ui/#/team/:orgId' },
  PROJECT_LIST: { path: '/ehi/#/estimates' },
  ADMIN: { path: '/admin/' },
  TERMS_OF_SERVICE: { path: 'https://hover.to/terms_of_use' },
  PROPOSALS_MANAGE: { path: 'https://hover.to/ehi/#/workflows/proposal' },
  PROPOSALS_LEARN_MORE: {
    path: 'https://help.hover.to/en/articles/5535283-proposal-management-customization',
  },
  ORDERING_LEARN_MORE: {
    path: 'https://hover.to/lp/hover-sdr/?utm_source=7014N000000XDgiQAG&campaignMedium=Website&campaignSource=Direct%20Ordering',
  },
  NEW_CONSTRUCTION_JOB_PAUSED: {
    path: 'https://help.hover.to/en/articles/6460725-why-was-my-new-construction-job-paused',
  },
  BLUEPRINT_TERMS: {
    path: '/ui/#/hvr/blueprint_terms',
  },
  CHECKLISTS_LEARN_MORE: {
    path: 'https://help.hover.to/en/articles/6619282-inspection-checklist',
  },
  CHECKLISTS_CUSTOMIZE_TEMPLATES: {
    path: '/ehi/#/workflows/inspection_templates',
  },
  MEASUREMENTS_UPGRADE_URL: {
    path: 'https://hover.to/lp/hover-sdr/?utm_source=7014N000002CRuAQAW',
  },
  ADVANCED_EXPORTS_UPGRADE_URL: {
    path: 'https://hover.to/lp/hover-sdr/3d-exports',
  },
  ESX_UPGRADE_URL: {
    path: 'https://hover.to/lp/hover-sdr/esx-exports',
  },
  JOB_ASSIGNMENTS: {
    path: '/ui/#/orgs/:orgId/jobs/:jobId/assign',
  },
  ORG_ASSIGNMENTS: {
    path: '/ui/#/orgs/:orgId/jobs/:jobId/accesses',
  },
  RAILS_ADMIN: {
    path: '/admin/jobs/:jobId',
  },
  JOIN_MOSAIC: {
    path: `https://tap.joinmosaic.com/?ref=hover19&hvr=:jobId`,
  },
  JOIN_MOSAIC_DEMO: {
    path: `https://tap.demo.joinmosaic.com/?ref=hover19&hvr=:jobId`,
  },
  EHI_CREATE_MATERIAL_LIST: {
    path: `/ehi/#/project/:jobId/scope?orgId=:orgId&materialListCreate=true`,
  },
  EHI_VIEW_MATERIAL_LIST: { path: '/ehi/#/project/:jobId/scope?orgId=:orgId' },
  EHI_CREATE_ESTIMATE: {
    path: `/ehi/#/estimator/questions/select_templates?jobId=:jobId`,
  },
  EHI_VIEW_CHECKLIST: {
    path: '/ehi/#/estimator/inspection_checklists/:checklistId/?orgId=:orgId&jobId=:jobId',
  },
  NEW_CONSTRUCTION_ADD_FILES: {
    path: '/ui/#/property/:jobId',
  },
  MEASUREMENTS_LEARN_MORE: {
    path: 'https://help.hover.to/en/collections/2633342-getting-started-with-hover',
  },
  MEASUREMENTS_PROCESSING_BLUEPRINTS_LEARN_MORE: {
    path: 'https://help.hover.to/en/articles/6383819-how-to-upload-a-blueprint',
  },
  PLAN: {
    path: '/account/plan/edit',
  },
  SETTINGS: {
    path: '/account/profile',
  },
  MANOWAR: {
    path: `https://:path.hover.to/orders/:jobManowarIdentifier`,
  },
  BILLING: {
    path: '/account/billing',
  },
  PAYMENTS: {
    path: '/settings/payment-method',
  },
} as const;

type MetadataKeys = (keyof PageViewed)[];

export interface RouteDescriptor {
  path: string;
  page?: Page;
  headerCallToAction?: HeaderCallToAction;
  metadata?: MetadataKeys;
}

type RouteKey =
  | 'SIGNUP_PROFESSIONAL_ACTIVATE'
  | 'DESIGN_STUDIO_EDIT'
  | 'DESIGN_PRO_EDIT'
  | 'DESIGN_STUDIO_GUEST'
  | 'DESIGN_STUDIO_PREVIEW'
  | 'DESIGN_STUDIO_PRO_VIEW'
  | 'DESIGN_STUDIO_SHARE_VIEW'
  | 'DESIGN_STUDIO'
  | 'AI_DESIGNER'
  | 'AI_DESIGNER_INSPIRATIONS'
  | 'AI_DESIGNER_SHARE'
  | 'INTERNAL'
  | 'INTERNAL_LOGOUT'
  | 'INTERNAL_PROFILE'
  | 'LOGIN'
  | 'LOGIN_BEACON'
  | 'LOGIN_BEACON_SUCCESS'
  | 'LOGIN_FORGOT'
  | 'LOGIN_FORGOT_SUCCESS'
  | 'LOGIN_FORGOT_PASSWORD'
  | 'LOGIN_SSO'
  | 'LOGIN_SSO_FAILURE'
  | 'LOGIN_SSO_SUCCESS'
  | 'NOT_FOUND'
  | 'LOGIN_RESET'
  | 'LOGIN_RESET_SUCCESS'
  | 'LOGIN_RESET_PASSWORD'
  | 'RENDERINGS_HUB'
  | 'RENDERINGS_SHARED'
  | 'RENDERINGS_USER_ACTIVATE'
  | 'RENDERINGS_INTERACTIVE'
  | 'ROOT'
  | 'SIGN_UP'
  | 'SIGNUP_MOBILE'
  | 'SIGN_UP_HOMEOWNER'
  | 'SIGN_UP_HOMEOWNER_DOWNLOAD'
  | 'SIGN_UP_HOMEOWNER_DOWNLOAD_SUCCESS'
  | 'SIGN_UP_INSURANCE'
  | 'SIGN_UP_INSURANCE_ORGANIZATION'
  | 'SIGN_UP_INSURANCE_SURVEY'
  | 'SIGN_UP_PROFESSIONAL'
  | 'SIGN_UP_PROFESSIONAL_PROMOTION_FB_20'
  | 'SIGN_UP_PROFESSIONAL_PROMOTION_FB_40'
  | 'SIGN_UP_PROFESSIONAL_PROMOTION_FB_60'
  | 'SIGN_UP_PROFESSIONAL_PROMOTION_FB_75'
  | 'SIGN_UP_PROFESSIONAL_ORGANIZATION'
  | 'SIGN_UP_PROFESSIONAL_SSO'
  | 'SIGN_UP_PROFESSIONAL_UPGRADE'
  | 'SIGN_UP_PROFESSIONAL_SURVEY'
  | 'NEW_CONSTRUCTION_UPLOAD'
  | 'SOMETHING_WRONG'
  | 'PROPERTY_DETAILS'
  | 'PROPERTIES'
  | 'PAINT_MEASUREMENTS'
  | 'PAINT_MEASUREMENTS_SHARED'
  | 'HOMEOWNER_APP_HOME'
  | 'HOMEOWNER_APP_FAVORITES'
  | 'HOMEOWNER_APP_SHARE'
  | 'HOMEOWNER_APP_ORIGINAL';

const designStudioMetadataKeys: MetadataKeys = ['job_id', 'partner_id'];

export const facebookPromotionRoutes = {
  SIGN_UP_PROFESSIONAL_PROMOTION_FB_20: {
    path: '/signup/professional/facebook-20',
    page: Page.PROFESSIONAL_ACCOUNT_PROMOTION__INPUT,
  },
  SIGN_UP_PROFESSIONAL_PROMOTION_FB_40: {
    path: '/signup/professional/facebook-40',
    page: Page.PROFESSIONAL_ACCOUNT_PROMOTION__INPUT,
  },
  SIGN_UP_PROFESSIONAL_PROMOTION_FB_60: {
    path: '/signup/professional/facebook-60',
    page: Page.PROFESSIONAL_ACCOUNT_PROMOTION__INPUT,
  },
  SIGN_UP_PROFESSIONAL_PROMOTION_FB_75: {
    path: '/signup/professional/facebook-75',
    page: Page.PROFESSIONAL_ACCOUNT_PROMOTION__INPUT,
  },
} as const;

const Route: { [key in RouteKey]: RouteDescriptor } = {
  SIGNUP_PROFESSIONAL_ACTIVATE: {
    path: '/signup/professional/activate/:token',
    page: Page.SIGNUP_PROFESSIONAL_ACTIVATE__INPUT,
  },
  DESIGN_STUDIO: {
    path: '/design-studio',
    page: Page.DESIGN_STUDIO,
  },
  DESIGN_STUDIO_SHARE_VIEW: {
    path: '/design-studio/shared/:id',
    page: Page.DESIGN_STUDIO_SHARE_VIEW,
    metadata: designStudioMetadataKeys,
  },
  DESIGN_STUDIO_PRO_VIEW: {
    path: '/design-studio/:id/user/:userId',
    page: Page.DESIGN_STUDIO_PRO_VIEW,
    metadata: designStudioMetadataKeys,
  },
  DESIGN_STUDIO_GUEST: {
    path: '/design-studio/guest',
    page: Page.DESIGN_STUDIO,
  },
  DESIGN_STUDIO_PREVIEW: {
    path: '/design-studio/preview',
    page: Page.DESIGN_STUDIO,
  },
  DESIGN_STUDIO_EDIT: {
    path: '/design-studio/:id',
    page: Page.DESIGN_STUDIO,
    metadata: designStudioMetadataKeys,
  },
  DESIGN_PRO_EDIT: {
    path: '/3d/:id',
    page: Page.DESIGN_PRO,
    metadata: designStudioMetadataKeys,
  },
  AI_DESIGNER_SHARE: {
    path: '/aidesigner/share',
    page: Page.AI_DESIGNER_SHARE,
  },
  AI_DESIGNER: {
    path: '/aidesigner',
    page: Page.AI_DESIGNER,
  },
  AI_DESIGNER_INSPIRATIONS: {
    path: '/aidesigner/:aiOriginalImageId/inspirations',
    page: Page.AI_DESIGNER_INSPIRATIONS,
  },
  HOMEOWNER_APP_HOME: {
    path: '/designer',
    page: Page.HOMEOWNER_APP_HOME,
  },
  HOMEOWNER_APP_FAVORITES: {
    path: '/designer/favorites',
    page: Page.HOMEOWNER_APP_FAVORITES,
  },
  HOMEOWNER_APP_SHARE: {
    path: '/designer/share',
    page: Page.HOMEOWNER_APP_SHARE,
  },
  HOMEOWNER_APP_ORIGINAL: {
    path: '/designer/original/:aiOriginalImageId',
    page: Page.HOMEOWNER_APP_ORIGINAL,
  },
  INTERNAL: { path: '/i' },
  INTERNAL_PROFILE: { path: '/i/profile' },
  INTERNAL_LOGOUT: { path: '/i/logout' },
  LOGIN: {
    headerCallToAction: HeaderCallToAction.SIGN_UP,
    path: '/login',
    page: Page.LOGIN__INPUT,
  },
  LOGIN_BEACON: {
    headerCallToAction: HeaderCallToAction.SIGN_UP,
    path: '/login/beacon',
    page: Page.BEACON_LOGIN__INPUT,
  },
  LOGIN_BEACON_SUCCESS: {
    path: '/login/beacon/success',
    page: Page.BEACON_LOGIN_SUCCESS,
  },
  LOGIN_FORGOT: {
    path: '/login/forgot',
    page: Page.FORGOT_PASSWORD__INPUT,
  },
  LOGIN_FORGOT_SUCCESS: {
    path: '/login/forgot/success',
    page: Page.PASSWORD_EMAIL_SENT,
  },
  LOGIN_FORGOT_PASSWORD: { path: '/login/forgot-password' },
  LOGIN_SSO: {
    headerCallToAction: HeaderCallToAction.SIGN_UP,
    path: '/login/sso',
    page: Page.SSO_LOGIN__INPUT,
  },
  LOGIN_SSO_FAILURE: {
    path: '/login/sso/failure',
    page: Page.SSO_LOGIN_FAILURE,
  },
  LOGIN_SSO_SUCCESS: {
    path: '/login/sso/success',
    page: Page.SSO_LOGIN_SUCCESS,
  },
  NOT_FOUND: { path: '/404' },
  LOGIN_RESET: {
    path: '/login/reset/:token',
    page: Page.RESET_PASSWORD__INPUT,
  },
  LOGIN_RESET_SUCCESS: {
    path: '/login/reset/success',
    page: Page.RESET_PASSWORD_SUCCESS,
  },
  LOGIN_RESET_PASSWORD: { path: '/login/reset-password/:token' },
  PROPERTIES: {
    path: '/wr/properties',
    page: Page.PROPERTIES,
  },
  PROPERTY_DETAILS: {
    path: '/wr/properties/:jobId/:tabId(estimation|checklists|measurements|ordering|photos|proposal|design)?',
    page: Page.PROPERTY_DETAILS,
  },
  NEW_CONSTRUCTION_UPLOAD: {
    path: '/wr/new-construction/upload',
    page: Page.NEW_CONSTRUCTION_UPLOAD,
  },
  RENDERINGS_HUB: {
    path: '/wr/properties/:jobId/renderings/:renderingId',
    page: Page.RENDERINGS_HUB,
  },
  RENDERINGS_SHARED: {
    path: '/wr/renderings/shared/:shareToken',
    page: Page.RENDERINGS_SHARED,
  },
  RENDERINGS_USER_ACTIVATE: {
    path: '/wr/renderings/user/activate/:token',
    page: Page.RENDERINGS_USER_ACTIVATE,
  },
  RENDERINGS_INTERACTIVE: {
    path: '/wr/renderings/:renderingId/design/:renderingFileId',
    page: Page.RENDERINGS_INTERACTIVE,
  },
  PAINT_MEASUREMENTS: {
    path: '/wr/measurements/:jobId/paint',
    page: Page.PAINT_MEASUREMENTS,
  },
  PAINT_MEASUREMENTS_SHARED: {
    path: '/wr/measurements/:jobId/paint/shared/:shareToken',
    page: Page.PAINT_MEASUREMENTS_SHARED,
  },
  ROOT: { path: '/' },
  SIGN_UP: { path: '/signup', page: Page.SIGNUP__FORK },
  SIGNUP_MOBILE: {
    headerCallToAction: HeaderCallToAction.NONE,
    path: '/signup/mobile',
  },
  SIGN_UP_HOMEOWNER: {
    path: '/signup/homeowner',
    page: Page.HOMEOWNER_ACCOUNT__INPUT,
  },
  SIGN_UP_HOMEOWNER_DOWNLOAD: {
    path: '/signup/homeowner/download',
    page: Page.HOMEOWNER_DOWNLOAD__INPUT,
  },
  SIGN_UP_HOMEOWNER_DOWNLOAD_SUCCESS: {
    path: '/signup/homeowner/download/success',
    page: Page.HOMEOWNER_DOWNLOAD_SENT,
  },
  SIGN_UP_INSURANCE: {
    path: '/signup/insurance',
    page: Page.INSURANCE_ACCOUNT__INPUT,
  },
  SIGN_UP_INSURANCE_ORGANIZATION: {
    path: '/signup/insurance/organization',
    page: Page.INSURANCE_COMPLETE_PROFILE__INPUT,
  },
  SIGN_UP_INSURANCE_SURVEY: {
    path: '/signup/insurance/survey',
    page: Page.INSURANCE_SURVEY_PROFILE__INPUT,
  },
  SIGN_UP_PROFESSIONAL: {
    path: '/signup/professional',
    page: Page.PROFESSIONAL_ACCOUNT__INPUT,
  },
  ...facebookPromotionRoutes,
  SIGN_UP_PROFESSIONAL_SSO: {
    path: '/signup/professional/sso',
    page: Page.SSO_EMAIL__INPUT,
  },
  SIGN_UP_PROFESSIONAL_ORGANIZATION: {
    path: '/signup/professional/organization',
    page: Page.COMPLETE_PROFILE__INPUT,
  },
  SIGN_UP_PROFESSIONAL_UPGRADE: {
    path: '/signup/professional/upgrade',
    page: Page.UPGRADE__INPUT,
  },
  SIGN_UP_PROFESSIONAL_SURVEY: {
    path: '/signup/professional/survey',
    page: Page.SURVEY_PROFILE__INPUT,
  },
  SOMETHING_WRONG: {
    path: '/something-wrong',
    page: Page.SOMETHING_WRONG,
  },
} as const;

const LOGIN_ROUTES = [
  Route.LOGIN.path,
  Route.LOGIN_SSO.path,
  Route.LOGIN_BEACON.path,
];
const LOGIN_REDIRECT_EXCLUSIONS = [
  Route.RENDERINGS_USER_ACTIVATE.path,
  Route.AI_DESIGNER.path,
  Route.AI_DESIGNER_SHARE.path,
  Route.HOMEOWNER_APP_HOME.path,
  Route.HOMEOWNER_APP_FAVORITES.path,
  Route.HOMEOWNER_APP_SHARE.path,
  Route.HOMEOWNER_APP_ORIGINAL.path,
  Route.PAINT_MEASUREMENTS_SHARED.path,
].concat(LOGIN_ROUTES);

export { External, Route, LOGIN_ROUTES, LOGIN_REDIRECT_EXCLUSIONS };
